import { useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';

import styles from './AdminNav.module.css';

export interface AdminNavItem {
  id: string;
  label: string;
  /** Optional red pip (e.g. dead-letter count). Rendered only when > 0. */
  badge?: number | null;
}

export interface AdminNavGroup {
  id: string;
  heading: string;
  items: AdminNavItem[];
}

interface Props {
  groups: AdminNavGroup[];
  activeGroupId: string;
  onGroupChange: (groupId: string) => void;
  onSectionChange: (sectionId: string) => void;
}

export function AdminNav({ groups, activeGroupId, onGroupChange, onSectionChange }: Props) {
  const activeGroup = groups.find((group) => group.id === activeGroupId) ?? groups[0];
  const allItems = useMemo(() => activeGroup?.items ?? [], [activeGroup]);
  const [observedId, setObservedId] = useState('');
  const hashId = window.location.hash.replace(/^#/, '');
  const fallbackId = allItems.some((item) => item.id === hashId)
    ? hashId
    : (allItems[0]?.id ?? '');
  const activeId = allItems.some((item) => item.id === observedId) ? observedId : fallbackId;

  useEffect(() => {
    const elements = allItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setObservedId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [allItems]);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setObservedId(id);
      onSectionChange(id);
    }
  };

  return (
    <nav className={styles.nav} aria-label="Admin sections">
      {groups.map((group) => (
        <div key={group.heading} className={styles.group}>
          <button
            type="button"
            className={`${styles.heading} ${group.id === activeGroupId ? styles.headingActive : ''}`}
            aria-expanded={group.id === activeGroupId}
            onClick={() => onGroupChange(group.id)}
          >
            {group.heading}
          </button>
          {group.id === activeGroupId && (
            <ul className={styles.list}>
              {group.items.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={`${styles.link} ${activeId === item.id ? styles.linkActive : ''}`}
                    onClick={(e) => handleClick(e, item.id)}
                  >
                    <span>{item.label}</span>
                    {item.badge != null && item.badge > 0 && (
                      <span className={styles.pip}>{item.badge}</span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </nav>
  );
}
